import { Injectable, signal } from '@angular/core';

export type NotificationType = 'success' | 'error' | 'warning';

export interface NotificationMessage {
  id: number;
  type: NotificationType;
  text: string;
  leaving?: boolean;
}

@Injectable({ providedIn: 'root' })
export class NotificationService {
  private _messages = signal<NotificationMessage[]>([]);
  messages = this._messages.asReadonly();

  private readonly ttlMs = 3000;
  private readonly exitMs = 220;

  private hideTimer: ReturnType<typeof setTimeout> | null = null;
  private removeTimer: ReturnType<typeof setTimeout> | null = null;

  show(text: string, type: NotificationType = 'success') {
    if (this.hideTimer) clearTimeout(this.hideTimer);
    if (this.removeTimer) clearTimeout(this.removeTimer);

    const existing = this._messages()[0];

    if (existing && !existing.leaving) {
      this._messages.set([{ ...existing, leaving: true }]);

      this.removeTimer = setTimeout(() => {
        this._messages.set([]);
        this._showFresh(text, type);
      }, this.exitMs);

      return;
    }

    this._showFresh(text, type);
  }

  private _showFresh(text: string, type: NotificationType) {
    const id = Date.now() + Math.floor(Math.random() * 1000);
    this._messages.set([{ id, text, type, leaving: false }]);

    this.hideTimer = setTimeout(() => this.dismiss(id), this.ttlMs - this.exitMs);
  }

  dismiss(id?: number) {
    const msg = this._messages()[0];
    if (!msg) return;
    if (id && msg.id !== id) return;
    if (msg.leaving) return;

    this._messages.set([{ ...msg, leaving: true }]);

    if (this.removeTimer) clearTimeout(this.removeTimer);
    this.removeTimer = setTimeout(() => {
      const current = this._messages()[0];
      if (current?.id === msg.id) this._messages.set([]);
    }, this.exitMs);
  }

  success(text: string) {
    this.show(text, 'success');
  }

  error(text: string) {
    this.show(text, 'error');
  }

  warning(text: string) {
    this.show(text, 'warning');
  }
}
